"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ZenlinkStableSwapProvider = void 0;
const chain_1 = require("@zenlink-interface/chain");
const currency_1 = require("@zenlink-interface/currency");
const ethers_1 = require("ethers");
const amm_1 = require("@zenlink-interface/amm");
const jsbi_1 = __importDefault(require("jsbi"));
const router_config_1 = require("@zenlink-interface/router-config");
const entities_1 = require("../entities");
const abis_1 = require("../abis");
const LiquidityProvider_1 = require("./LiquidityProvider");
const StablePools = {
    [chain_1.ParachainId.ASTAR]: [
        // [4pool, lp]
        ['0xb0Fa056fFFb74c0FB215F86D691c94Ed45b686Aa', '0x755cbAC2246e8219e720591Dd362a772076ab653'],
    ],
};
class ZenlinkStableSwapProvider extends LiquidityProvider_1.LiquidityProvider {
    constructor(chainId, client) {
        super(chainId, client);
        this.poolCodes = [];
        this.fee = 0.0005;
        this.fetchedTokens = new Set();
    }
    getType() {
        return LiquidityProvider_1.LiquidityProviders.ZenlinkStableSwap;
    }
    getPoolProviderName() {
        return 'Zenlink';
    }
    async getPools(tokens) {
        if (StablePools[this.chainId] === undefined) {
            this.lastUpdateBlock = -1;
            return [];
        }
        const [, poolsStable] = await this.getStablePools(tokens);
        if (poolsStable.length) {
            this.poolCodes = [...this.poolCodes, ...poolsStable];
            ++this.stateId;
        }
    }
    async getStablePools(tokens, needPoolCode = true) {
        // create token map: token address => token
        const tokenMap = new Map();
        tokens.forEach((t) => {
            if (!this.fetchedTokens.has(t))
                this.fetchedTokens.add(t);
            tokenMap.set(t.address.toLowerCase(), t);
        });
        const [poolAddresses, lpAddresses] = StablePools[this.chainId]?.reduce((memo, pool) => {
            const [poolAddress, lpAddress] = pool;
            memo[0].push(poolAddress);
            memo[1].push(lpAddress);
            return memo;
        }, [[], []]) || [];
        const poolTokensPromise = this.client.multicall({
            multicallAddress: this.client.chain?.contracts?.multicall3?.address,
            allowFailure: true,
            contracts: poolAddresses.map(addr => ({
                address: addr,
                chainId: chain_1.chainsParachainIdToChainId[this.chainId],
                abi: abis_1.zenlinkStableSwap,
                functionName: 'getTokens',
            })),
        });
        const tokenBalancesPromise = this.client.multicall({
            multicallAddress: this.client.chain?.contracts?.multicall3?.address,
            allowFailure: true,
            contracts: poolAddresses.map(addr => ({
                address: addr,
                chainId: chain_1.chainsParachainIdToChainId[this.chainId],
                abi: abis_1.zenlinkStableSwap,
                functionName: 'getTokenBalances',
            })),
        });
        const swapStoragePromise = this.client.multicall({
            multicallAddress: this.client.chain?.contracts?.multicall3?.address,
            allowFailure: true,
            contracts: poolAddresses.map(addr => ({
                address: addr,
                chainId: chain_1.chainsParachainIdToChainId[this.chainId],
                abi: abis_1.zenlinkStableSwap,
                functionName: 'swapStorage',
            })),
        });
        const getAPromise = this.client.multicall({
            multicallAddress: this.client.chain?.contracts?.multicall3?.address,
            allowFailure: true,
            contracts: poolAddresses.map(addr => ({
                address: addr,
                chainId: chain_1.chainsParachainIdToChainId[this.chainId],
                abi: abis_1.zenlinkStableSwap,
                functionName: 'getA',
            })),
        });
        const getVirtualPricePromise = this.client.multicall({
            multicallAddress: this.client.chain?.contracts?.multicall3?.address,
            allowFailure: true,
            contracts: poolAddresses.map(addr => ({
                address: addr,
                chainId: chain_1.chainsParachainIdToChainId[this.chainId],
                abi: abis_1.zenlinkStableSwap,
                functionName: 'getVirtualPrice',
            })),
        });
        const totalSupplysPromise = this.client.multicall({
            multicallAddress: this.client.chain?.contracts?.multicall3?.address,
            allowFailure: true,
            contracts: lpAddresses.map(addr => ({
                address: addr,
                chainId: chain_1.chainsParachainIdToChainId[this.chainId],
                abi: abis_1.zenlinkStableSwap,
                functionName: 'totalSupply',
            })),
        });
        const [pooledTokens, tokenBalances, swapStorage, A, virtualPrices, totalSupplys,] = await Promise.all([
            poolTokensPromise,
            tokenBalancesPromise,
            swapStoragePromise,
            getAPromise,
            getVirtualPricePromise,
            totalSupplysPromise,
        ]);
        const poolCodes = [];
        const stableSwaps = [];
        poolAddresses.forEach((addr, i) => {
            const lpToken = lpAddresses[i];
            const tokens = pooledTokens[i]?.result;
            const balances = tokenBalances[i]?.result;
            const storage = swapStorage[i]?.result;
            const a = ethers_1.BigNumber.from(A[i].result);
            const virtualPrice = ethers_1.BigNumber.from(virtualPrices[i].result);
            const totalSupply = ethers_1.BigNumber.from(totalSupplys[i].result);
            if (tokens
                && lpToken
                && balances
                && storage
                && a
                && virtualPrice
                && totalSupply
                && tokens.every(addr => tokenMap.get(addr.toLowerCase()) !== undefined)) {
                const liquidityToken = new currency_1.Token({
                    chainId: this.chainId,
                    address: lpToken,
                    decimals: 18,
                    symbol: '',
                    name: '',
                });
                const pooledTokens = tokens.map(address => tokenMap.get(address.toLowerCase()));
                const swap = new amm_1.StableSwap(this.chainId, addr, pooledTokens, liquidityToken, currency_1.Amount.fromRawAmount(liquidityToken, totalSupply.toString()), balances.map((balance, i) => currency_1.Amount.fromRawAmount(pooledTokens[i], ethers_1.BigNumber.from(balance).toString())), jsbi_1.default.BigInt(ethers_1.BigNumber.from(storage[1]).toString()), jsbi_1.default.BigInt(ethers_1.BigNumber.from(storage[2]).toString()), jsbi_1.default.BigInt(a.toString()), jsbi_1.default.BigInt(virtualPrice.toString()));
                stableSwaps.push(swap);
                if (needPoolCode) {
                    for (let i = 0; i < pooledTokens.length; i++) {
                        for (let j = i + 1; j < pooledTokens.length; j++) {
                            const pool = new entities_1.StablePool(swap, pooledTokens[i], pooledTokens[j], this.fee);
                            poolCodes.push(new entities_1.StablePoolCode(pool, this.getPoolProviderName()));
                            ++this.stateId;
                        }
                    }
                }
            }
        });
        return [stableSwaps, poolCodes];
    }
    async updatePoolsData() {
        if (!this.poolCodes.length)
            return;
        const pools = this.poolCodes.map(pc => pc.pool).filter(p => p instanceof entities_1.StablePool);
        const [stableSwaps] = await this.getStablePools(Array.from(this.fetchedTokens), false);
        const stableSwapMap = new Map();
        stableSwaps.forEach((swap) => {
            stableSwapMap.set(swap.contractAddress, swap);
        });
        pools.forEach((pool) => {
            const swap = stableSwapMap.get(pool.swap.contractAddress);
            if (swap) {
                pool.updateSwap(swap);
                ++this.stateId;
            }
        });
    }
    _getProspectiveTokens(t0, t1) {
        const set = new Set([
            t0,
            t1,
            ...router_config_1.BASES_TO_CHECK_TRADES_AGAINST[this.chainId],
            ...(router_config_1.ADDITIONAL_BASES[this.chainId]?.[t0.address] || []),
            ...(router_config_1.ADDITIONAL_BASES[this.chainId]?.[t1.address] || []),
        ]);
        return Array.from(set);
    }
    startFetchPoolsData() {
        this.stopFetchPoolsData();
        this.poolCodes = [];
        this.fetchedTokens.clear();
        this.getPools(router_config_1.BASES_TO_CHECK_TRADES_AGAINST[this.chainId]); // starting the process
        this.unwatchBlockNumber = this.client.watchBlockNumber({
            onBlockNumber: (blockNumber) => {
                this.lastUpdateBlock = Number(blockNumber);
                this.updatePoolsData();
            },
            onError: (error) => {
                console.error(error.message);
            },
        });
    }
    async fetchPoolsForToken(t0, t1) {
        await this.getPools(this._getProspectiveTokens(t0, t1));
    }
    getCurrentPoolList() {
        return this.poolCodes;
    }
    stopFetchPoolsData() {
        if (this.unwatchBlockNumber)
            this.unwatchBlockNumber();
    }
}
exports.ZenlinkStableSwapProvider = ZenlinkStableSwapProvider;
